import { CheckCircle2, XCircle, Phone, ArrowUpRight, MessageCircle, HandHeart, Ban, Lock } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F2EDE2";
const PAPER = "#FFFEFB";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const RED_MUTED = "#A85050";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const CHEAP_QUOTE = ["Single-spring replacement only — partner spring fails within months", "Cheap 10,000-cycle imported springs as standard", "Skipped balance test — door binds within weeks", "Verbal quote on phone, &lsquo;extras&rsquo; added on-site", "Warranty: &lsquo;We&rsquo;ll come back if there&rsquo;s an issue&rsquo; (nothing in writing)"];
const OUR_QUOTE = ["Matched pair always replaced together — same cycle life", "20,000+ cycle commercial-grade steel as standard", "4-position balance test, documented on warranty card", "Written fixed-price quote before any tool comes out", "12-month workmanship warranty + physical warranty card"];
const PLEDGES = [{
  icon: Ban,
  text: "No callout fee — ever"
}, {
  icon: HandHeart,
  text: "Tell us your budget, we&rsquo;ll work with you"
}, {
  icon: Lock,
  text: "Don&rsquo;t like the quote? You walk away free"
}];
export default function SpringPricing() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY
  }} aria-labelledby="spring-pricing-heading">
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.05 0 0 0 0 0.08 0 0 0 0 0.22 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      <div aria-hidden className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full pointer-events-none opacity-[0.05]" style={{
      backgroundColor: GOLD,
      filter: "blur(80px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        {/* Top strip */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-14 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD_DARK}66`,
            backgroundColor: `${GOLD}1A`
          }}>
              <HandHeart className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD_DARK
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD_DARK
          }}>
              Fair Pricing · Honest Quotes
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${NAVY}26`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: INK_MUTED
        }}>
            What you&rsquo;re actually paying for
          </div>
        </div>

        {/* Headline */}
        <div className="mb-14 md:mb-16 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          <h2 id="spring-pricing-heading" className="lg:col-span-9 font-extrabold" style={{
          fontFamily: FONT_HEAD,
          color: NAVY,
          fontSize: "clamp(2rem, 5vw, 4rem)",
          lineHeight: 1.02,
          letterSpacing: "-0.03em"
        }}>
            A cheap spring quote and a fair one
            <br className="hidden md:block" />
            don&rsquo;t cost the same{" "}
            <span style={{
            color: GOLD_DARK
          }}>in year three.</span>
          </h2>
          <p className="lg:col-span-3 text-[14px] leading-relaxed" style={{
          color: INK_MUTED
        }}>
            We don&rsquo;t post prices online &mdash; spring jobs vary too much.
            But here&rsquo;s exactly what you&rsquo;re comparing when you collect
            three quotes.
          </p>
        </div>

        {/* === COMPARISON GRID === */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-12 md:mb-14">

          {/* LEFT — Cheap quote */}
          <article className="relative rounded-2xl border overflow-hidden" style={{
          backgroundColor: CREAM_DEEP,
          borderColor: `${INK_MUTED}33`
        }}>
            <div className="p-6 md:p-7">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border" style={{
                  borderColor: `${RED_MUTED}66`,
                  backgroundColor: `${RED_MUTED}14`
                }}>
                    <XCircle className="h-[18px] w-[18px]" strokeWidth={2.2} style={{
                    color: RED_MUTED
                  } as React.CSSProperties} />
                  </span>
                  <div className="text-[10px] font-bold tracking-[0.22em] uppercase" style={{
                  color: RED_MUTED
                }}>
                    What others quote
                  </div>
                </div>
              </div>

              <h3 className="font-extrabold mb-5" style={{
              fontFamily: FONT_HEAD,
              color: INK_MUTED,
              fontSize: "clamp(1.25rem, 2.2vw, 1.5rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              textDecoration: "line-through",
              textDecorationColor: `${RED_MUTED}80`,
              textDecorationThickness: "2px"
            }}>
                The cheap quote
              </h3>

              <ul className="space-y-3.5">
                {CHEAP_QUOTE.map((item, idx) => <li key={idx} className="flex items-start gap-3">
                    <XCircle className="flex-shrink-0 mt-0.5 h-[18px] w-[18px]" strokeWidth={2} style={{
                  color: `${RED_MUTED}99`
                } as React.CSSProperties} />
                    <span className="text-[14px] leading-[1.55]" style={{
                  color: `${INK_MUTED}E6`
                }} dangerouslySetInnerHTML={{
                  __html: item
                }} />
                  </li>)}
              </ul>
            </div>
          </article>

          {/* RIGHT — Our quote */}
          <article className="relative rounded-2xl border overflow-hidden" style={{
          backgroundColor: PAPER,
          borderColor: `${GOLD_DARK}40`,
          boxShadow: `0 20px 60px -20px ${NAVY}26`
        }}>
            <div aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}80 50%, transparent 100%)`
          }} />

            <div className="p-6 md:p-7">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full" style={{
                  backgroundColor: GOLD,
                  border: `1px solid ${GOLD}`,
                  boxShadow: `0 6px 20px -6px ${GOLD}99`
                }}>
                    <CheckCircle2 className="h-[18px] w-[18px]" strokeWidth={2.5} style={{
                    color: NAVY
                  } as React.CSSProperties} />
                  </span>
                  <div className="text-[10px] font-bold tracking-[0.22em] uppercase" style={{
                  color: GOLD_DARK
                }}>
                    What we quote
                  </div>
                </div>

                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[9px] font-extrabold tracking-[0.18em] uppercase" style={{
                backgroundColor: NAVY,
                color: GOLD,
                fontFamily: FONT_BODY
              }}>
                  Worth Paying For
                </span>
              </div>

              <h3 className="font-extrabold mb-5" style={{
              fontFamily: FONT_HEAD,
              color: NAVY,
              fontSize: "clamp(1.25rem, 2.2vw, 1.5rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em"
            }}>
                The Gold Garage Door quote
              </h3>

              <ul className="space-y-3.5">
                {OUR_QUOTE.map((item, idx) => <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="flex-shrink-0 mt-0.5 h-[18px] w-[18px]" strokeWidth={2.5} style={{
                  color: GOLD_DARK
                } as React.CSSProperties} />
                    <span className="text-[14px] leading-[1.55] font-medium" style={{
                  color: INK
                }}>
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </article>
        </div>

        {/* === Trust pledges + CTA === */}
        <div className="relative rounded-2xl overflow-hidden p-6 md:p-8" style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`
      }}>
          <div aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{
          background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}80 50%, transparent 100%)`
        }} />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-[10px] font-bold tracking-[0.28em] uppercase mb-4" style={{
              color: GOLD
            }}>
                Our promise either way
              </div>
              <ul className="space-y-2.5">
                {PLEDGES.map(({
                icon: Icon,
                text
              }) => <li key={text} className="flex items-center gap-3 text-white">
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg" style={{
                  backgroundColor: `${GOLD}1F`,
                  border: `1px solid ${GOLD}40`
                }}>
                      <Icon className="h-[15px] w-[15px]" strokeWidth={2} style={{
                    color: GOLD
                  } as React.CSSProperties} />
                    </span>
                    <span className="text-[14px] md:text-[15px] font-medium" style={{
                  color: "rgba(255,255,255,0.92)"
                }} dangerouslySetInnerHTML={{
                  __html: text
                }} />
                  </li>)}
              </ul>
            </div>

            <div className="lg:col-span-5 flex flex-col gap-3">
              <a href="tel:0485000933" className="group inline-flex items-center justify-center gap-2.5 rounded-xl px-5 py-4 text-[14px] font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_BODY,
              boxShadow: `0 14px 36px -10px ${GOLD}99`
            }}>
                <Phone className="h-4 w-4" strokeWidth={2.5} />
                Call 0485 000 933 for a quote
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
              </a>

              <a href="https://wa.me/61485000933" className="group inline-flex items-center justify-center gap-2 rounded-xl border-2 px-5 py-3.5 text-[14px] font-semibold text-white transition-all duration-200" style={{
              borderColor: "rgba(255,255,255,0.30)",
              fontFamily: FONT_BODY
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.10)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.55)";
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.30)";
            }}>
                <MessageCircle className="h-4 w-4" strokeWidth={2} />
                Or WhatsApp us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}